import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLoad } from '../shared/hooks/load'

const PageContext = React.createContext()

export const usePageContext = () => useContext(PageContext)

const LOCALHOST = 'localhost'

export const THEME_TYPE_SYSTEM = 'system'
export const THEME_TYPE_LIGHT = 'light'
export const THEME_TYPE_DARK = 'dark'

export const PageProvider = ({ children }) => {
  ///////////HOST///////////
  const [hostname, updateHostname] = useState()

  useLoad(() => updateHostname(window.location.hostname), [window.location])

  const host = {
    isLocalhost: hostname === LOCALHOST
  }
  ///////////HOST///////////

  ///////////THEME///////////
  const [themeType, updateThemeType] = useState()
  const [isDark, updateIsDark] = useState(false)

  const hasSystemTheme = useMemo(() => window.matchMedia('(prefers-color-scheme)').matches, [])

  const media = useMemo(() => window.matchMedia('(prefers-color-scheme: dark)'), [])

  const setMediaAttribute = useCallback(media => {
    if (media.matches) updateIsDark(true)
    else updateIsDark(false)
  }, [])

  useLoad(() => {
    const theme = localStorage.getItem('theme')
    if (theme) {
      updateThemeType(theme)
    } else {
      if (hasSystemTheme) {
        updateThemeType(THEME_TYPE_SYSTEM)
      } else {
        updateThemeType(THEME_TYPE_LIGHT)
      }
    }
  }, [])

  useLoad(
    () => {
      if (themeType) {
        localStorage.setItem('theme', themeType)
        switch (themeType) {
          case THEME_TYPE_DARK:
            updateIsDark(true)
            break
          case THEME_TYPE_LIGHT:
            updateIsDark(false)
            break
          case THEME_TYPE_SYSTEM:
          default:
            setMediaAttribute(media)
        }
      }
    },
    [themeType]
  )

  // Only System follows the computer when its appearance changes: a chosen
  // Light or Dark stays put. It used to follow it whatever was chosen, so a
  // Mac set to Auto turning dark at sunset took a Light admin dark, and
  // choosing Light again did nothing until a reload (found 2026-11-02).
  useEffect(() => {
    if (themeType !== THEME_TYPE_SYSTEM) return undefined
    const follow = change => setMediaAttribute(change)
    media.addEventListener('change', follow)
    return () => media.removeEventListener('change', follow)
  }, [themeType, media, setMediaAttribute])

  useLoad(
    () => {
      if (isDark) {
        document.body.setAttribute('data-bs-theme', THEME_TYPE_DARK)
      } else {
        document.body.setAttribute('data-bs-theme', THEME_TYPE_LIGHT)
      }
    },
    [isDark]
  )

  const theme = {
    actions: {
      setThemeLight: () => updateThemeType(THEME_TYPE_LIGHT),
      setThemeDark: () => updateThemeType(THEME_TYPE_DARK),
      setThemeSystem: () => updateThemeType(THEME_TYPE_SYSTEM)
    },
    type: themeType,
    isDark: isDark,
    hasSystemTheme: hasSystemTheme
  }

  ///////////THEME///////////

  return (
    <PageContext.Provider value={{ theme, host }}>
      {children}
    </PageContext.Provider>
  )
}
